import React, { useEffect, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import VlrIcon from '../assets/png/vlr-token-icon.png';
import { ReactComponent as CloseButtonDark } from '../assets/svg/modal_close_btn-dark.svg';
import { ReactComponent as CloseButtonLight } from '../assets/svg/modal_close_btn-light.svg';
import ClaimConfirmationModal from './ClaimConfirmationModal';
import modalTextureDark from '../assets/png/modal_texture-dark.png';
import modalTextureLight from '../assets/png/modal_texture-light.png';
import { useAccount, useWaitForTransactionReceipt } from 'wagmi';
import availableRewardsBgDark from '../assets/png/available_rewards_bg-dark.png';
import availableRewardsBgLight from '../assets/png/available_rewards_bg-light.png';
import { useClaimWeeks } from '../claimer/useClaim';
import { useTotalWeiClaimable } from '../claimer/useTotalWeiClaimable';
import { Skeleton } from '@mui/material';
import { Spinner } from './Spinner';
import { isUserRejectedError } from '../utils/errors';
import { useClaimableParticipations } from '../claimer/useClaimableParticipations';

interface ClaimVlrModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ClaimVlrModal: React.FC<ClaimVlrModalProps> = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const { address, chainId } = useAccount();
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const {
    formatted: formattedClaimable,
    totalWeiClaimable,
    refetch: refetchClaimable,
  } = useTotalWeiClaimable();
  const {
    claim,
    txHash,
    isPending: isTxSubmitting,
    isError: isTxSubmittingError,
    error: txSubmittingError,
    reset,
  } = useClaimWeeks();
  const {
    isLoading: isTxReceiptLoading,
    isError: isTxReceiptError,
    error: txReceiptError,
    // data: txReceipt,
    isSuccess: isClaimTxReceiptSuccess,
  } = useWaitForTransactionReceipt({
    hash: txHash,
    confirmations: 3,
  });

  const cliamableParticipations = useClaimableParticipations();
  const shouldShowSpinner = isTxSubmitting || isTxReceiptLoading;

  const userRejected = isUserRejectedError(txSubmittingError);

  const errorMessage = userRejected
    ? 'Claim transaction rejected. Please try again'
    : !!txSubmittingError || !!txReceiptError || isTxSubmittingError || isTxReceiptError
      ? 'Claim transaction failed. Please try again'
      : null;

  useEffect(() => {
    if (!isOpen) {
      // setErrorMessage(null); // Reset error when modal closes
      reset(); // Reset claim state when modal closes
    }
  }, [isOpen, reset]);
  useEffect(() => {
    if (isClaimTxReceiptSuccess) {
      //debugger;
      // Refetch claimable amount after successful claim
      refetchClaimable();
      setShowConfirmation(true);
    }
  }, [isClaimTxReceiptSuccess, refetchClaimable]);
  // console.log("ClaimVlrModal - isPending:", isPending, "isError:", isError, "error:", error);
  if (!isOpen) return null;
  const isClaimButtonDisabled = chainId !== 8453 || shouldShowSpinner || !totalWeiClaimable;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleClaim = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();

    // Check if address is available
    if (!address) {
      throw new Error('Wallet not connected');
    }

    // const campaignCode: CampaignCode = 'road-to-velora';
    // const campaignRoadToVelora = getCampaignByCode(campaignCode);
    const participations = cliamableParticipations;
    console.log('Claiming VLR tokens...', participations);

    // Check if user has any eligible participations
    if (!participations || Object.keys(participations).length === 0) {
      console.error('No eligible participations found for address:', address);
      throw new Error('No eligible participations found for your address.');
    }

    // Additional safety check for campaign data
    // if (!campaignRoadToVelora) {
    //   console.error('Campaign not found:', campaignCode);
    //   throw new Error('Campaign data not found.');
    //   // return;
    // }

    // const participation = Object.values(participations)[0]; // TODO: Fix this.. There is only one participation per campagin by done this so we don't break getUserTotalClaimableVlrWei()

    // Clear any previous error messages
    // setErrorMessage("");

    // Proceed with claim if validation passes
    claim({
      chainId: Object.values(participations)[0].campaign.chain_id,
      claimerContractAddress: Object.values(participations)[0].campaign.contract_address,
      input: Object.values(participations).map((participation) => ({
        week: participation.campaign.week,
        amount: participation.amount.toString(),
        merkleProof: participation.proofs,
        participant: address,
      })),
    });

    // Show confirmation modal and hide claim modal
    // setShowConfirmation(true);
  };

  const handleConfirmationClose = (): void => {
    setShowConfirmation(false);
    onClose(); // Close the entire flow
  };

  // If confirmation is showing, don't render the claim modal
  if (showConfirmation) {
    return <ClaimConfirmationModal isOpen={true} onClose={handleConfirmationClose} />;
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-85 flex items-end md:items-center justify-center z-[9999]"
      onClick={handleBackdropClick}
    >
      {/* Gradient border wrapper */}
      <div className="relative w-full md:w-[400px] h-auto bg-gradient-to-b from-white/20 via-white/10 to-white/5 dark:from-white/10 dark:via-white/5 dark:to-transparent rounded-t-[16px] md:rounded-[16px] p-[1px] max-w-full md:max-w-none">
        <div
          className="relative w-full h-full bg-contrast-8 dark:bg-contrast-2 rounded-t-[15px] md:rounded-[15px] overflow-hidden"
          style={{
            backgroundImage:
              theme === 'dark' ? `url(${modalTextureDark})` : `url(${modalTextureLight})`,
            backgroundSize: theme === 'dark' ? '100% auto' : 'auto 120px',
            backgroundPosition:
              theme === 'dark' ? 'right -16px bottom -11px' : 'right -16px bottom -11px',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {/* Content Wrapper */}
          <div className="p-6 h-full flex flex-col items-center relative z-10">
            {/* Close Button */}
            <div className="w-full flex justify-end mb-4">
              <button
                onClick={onClose}
                className="w-[40px] h-[40px] flex items-center justify-center"
              >
                {theme === 'dark' ? (
                  <CloseButtonDark className="w-[40px] h-[40px]" />
                ) : (
                  <CloseButtonLight className="w-[40px] h-[40px]" />
                )}
              </button>
            </div>

            {/* Available Rewards Section */}
            <div
              className="rounded-[16px] w-[352px] h-auto flex items-center justify-center relative mb-4 p-[1px]"
              style={{
                background:
                  'linear-gradient(45deg, rgba(247, 92, 42, 0.8) 0%, rgba(74, 93, 249, 0.8) 100%)',
                boxShadow: '0 2px 12px 0 rgba(15, 17, 26, 0.05)',
              }}
            >
              <div
                className="rounded-[15px] w-full h-auto p-6 flex items-center justify-center relative overflow-hidden bg-contrast-9 dark:bg-contrast-1"
                style={{
                  backgroundImage:
                    theme === 'dark'
                      ? `url(${availableRewardsBgDark})`
                      : `url(${availableRewardsBgLight})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                {/* Content */}
                <div className="flex flex-col items-center gap-4 w-[335px] relative z-10">
                  <div className="text-contrast-4 dark:text-contrast-7 text-[14px] font-inter text-center">
                    Available Rewards
                  </div>
                  <img src={VlrIcon} alt="VLR Token" className="w-10 h-10" />
                  <div className="text-contrast-3 dark:text-contrast-9 text-[24px] font-medium font-geist-mono text-center w-[324px]">
                    {formattedClaimable ?? <Skeleton width={80} />}
                  </div>
                </div>
              </div>
            </div>

            {/* Description Text */}
            <p className="text-contrast-5 dark:text-contrast-6 text-[14px] font-geist text-center leading-[22px] mb-6 w-[352px]">
              Once you claim your $VLR tokens, you will be able to stake them in order to receive
              more rewards and other perks
            </p>

            {/* Error Message */}
            {errorMessage && (
              <div className="w-[352px] mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-[6px]">
                <p className="text-red-600 dark:text-red-400 text-[14px] font-geist text-center">
                  {errorMessage}
                </p>
              </div>
            )}

            {/* Claim Button */}
            <button
              disabled={isClaimButtonDisabled}
              type="button"
              onClick={handleClaim}
              className="w-[352px] h-12 bg-primary-papaya hover:bg-opacity-90 hover:shadow-none text-white font-medium font-inter text-[14px] rounded-[8px] transition-all shadow-[0_2px_12px_0_rgba(255,158,0,0.20),0_0_14px_0_rgba(255,158,0,0.60)_inset,0_0_16px_0_rgba(90,63,229,0.15)] mb-6 relative z-10 cursor-pointer flex items-center justify-center"
            >
              {shouldShowSpinner ? <Spinner /> : 'Claim $VLR'}
            </button>

            {/* Pagination Indicator */}
            <div className="flex items-center justify-center gap-1">
              <div className="w-2 h-2 bg-primary-papaya rounded-full"></div>
              <div className="w-2 h-2 bg-contrast-7 dark:bg-contrast-4 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimVlrModal;
